import React, { memo, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  makeStyles,
  Typography,
} from 'transfix-ui/core'
import { Dialog } from 'components/layout'
import { PlusIcon } from 'transfix-icons'
import { brandColors, Tab, Tabs } from 'transfix-ui'
import { CodeEditor } from './Editor/CodeEditor'
import { ParserVersion } from './types'

export type TSidePanelType = 'extraction' | 'conversion'

interface IInputSidePanel {
  /**
   * @example 'Extract Data'
   */
  title: string
  /**
   * Either 'conversion' or 'extraction'
   * @example 'conversion'
   */
  type: TSidePanelType
  sidebarOpen: boolean
  /**
   * Data that should populate the input form
   */
  inputData?: string | null
  onInputDataChange: (inputData: string) => void
  /**
   * The data that users will need to see to construct their input data
   */
  referenceData?: string
  /**
   * The parser response (either extraction or conversion)
   */
  outputData?: string
  onSubmit: () => void
  loading: boolean
  /**
   * Any errors returned by the parser (either extraction or conversion)
   */
  outputErrors?: string[] | null
  debugLogs?: string[][] | null
  /**
   * Function that will navigate user to the next step in process
   */
  onTransitionToNextStep: () => void
  onClose: () => void
  /**
   * Parser that has been loaded/being currently edited.
   * @example { id: 1, is_live: false, was_ever_live: false, extraction_template: 'Some jsonata query', conversion_instructions: 'Some json'}
   */
  currentParserVersion?: ParserVersion
}

const useStyles = makeStyles(theme => ({
  button: {
    marginRight: theme.spacing(1),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  log: {
    whiteSpace: 'pre',
  },
  explanatoryText: {
    marginBottom: theme.spacing(1),
  },
  editor: {
    marginRight: theme.spacing(4),
    borderRight: 'solid 1px',
  },
  successText: {
    color: brandColors.green3,
  },
  sidePanel: {
    width: '95vw !important',
  },
  submitButton: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(7),
  },
  modal: {
    float: 'left',
  },
  errorMessage: {
    color: brandColors.error,
  },
  accordianDetails: {
    display: 'block',
  },
  row: {
    width: '100%',
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'left',
    justifyContent: 'left',
  },
}))

const SIDEBAR_TABS = {
  input: 'Input',
  logs: 'Logs',
}

export const InputSidePanel = memo<IInputSidePanel>(
  ({
    debugLogs,
    inputData,
    loading,
    onClose,
    onInputDataChange,
    onSubmit,
    onTransitionToNextStep,
    outputData,
    outputErrors,
    referenceData,
    sidebarOpen,
    title,
    type,
  }) => {
    const navigateToNextSidePanel = () => {
      onTransitionToNextStep()
    }

    const closeDialogHandler = () => {
      onClose()
    }

    const classes = useStyles()

    const [tabState, setTabState] = useState(SIDEBAR_TABS.input)

    return (
      <>
        <Dialog
          id={`input-side-panel-${type}`}
          align='right'
          className={classes.sidePanel}
          closeDialog={closeDialogHandler}
          dialogOpen={sidebarOpen}>
          <Tabs value={tabState} onChange={setTabState}>
            <Tab label='Input' value={SIDEBAR_TABS.input}>
              <Box className={classes.title}>
                <Typography variant='h2'>{title}</Typography>
              </Box>
              <Box className={classes.explanatoryText}>
                {type === 'extraction' && (
                  <Typography variant='body1'>
                    Enter a{' '}
                    <Link
                      href='https://www.stedi.com/docs/mappings/jsonata-cheatsheet'
                      target='_blank'
                      rel='noopener noreferrer'>
                      JSONata
                    </Link>{' '}
                    query below (middle panel) to extract the transformed JSON (left panel).
                  </Typography>
                )}
                {type === 'conversion' && (
                  <Typography variant='body1'>
                    Write conversion instructions below (middle panel) to map the extracted JSON
                    (left panel)
                  </Typography>
                )}
              </Box>
              <Box ml={2} className={classes.row}>
                <Box>
                  {type === 'extraction'
                    ? 'Transformed JSON (reference)'
                    : 'Extracted JSON (reference)'}
                  <CodeEditor height='60vh' width='30vw' value={referenceData} language='json' />
                </Box>
                <Box>
                  {type === 'extraction' ? 'Jsonata Query' : 'Conversion Instructions'}
                  <CodeEditor
                    height='60vh'
                    width='30vw'
                    value={inputData || undefined}
                    language={type === 'extraction' ? 'plaintext' : 'json'}
                    onInputDataChange={val => onInputDataChange(val)}
                  />
                  <Box>
                    <Button
                      className={classes.submitButton}
                      size='medium'
                      onClick={onSubmit}
                      disabled={loading}>
                      {type === 'conversion'
                        ? 'Submit conversion instructions'
                        : 'Run Jsonata query'}
                    </Button>
                  </Box>
                </Box>
                <Box ml={2}>
                  Output
                  <CodeEditor
                    height='60vh'
                    width='30vw'
                    value={loading ? 'Loading ...' : outputData}
                    language='json'
                  />
                </Box>
              </Box>
              {type === 'extraction' &&
                outputData !== undefined &&
                !loading &&
                outputErrors === null && (
                  <Box>
                    <Typography className={classes.successText}>
                      Your JSONata query succesfully extracted data (see below). The next step is to
                      specify <Link onClick={navigateToNextSidePanel}>conversion instructions</Link>
                    </Typography>
                  </Box>
                )}
              <Box>
                {outputErrors?.map((error, index) => {
                  return (
                    <Typography color='textSecondary' className={classes.errorMessage}>
                      {type === 'extraction' ? `${error}` : `Error in Shipment ${index}: ${error}`}
                    </Typography>
                  )
                })}
              </Box>
            </Tab>
            {type === 'conversion' && (
              <Tab label='Logs' value={SIDEBAR_TABS.logs}>
                <Box>
                  {!debugLogs && <Typography>No Debug Logs Yet ...</Typography>}
                  {debugLogs?.map((logs, index) => {
                    return (
                      <Box mb={2}>
                        <Accordion>
                          <AccordionSummary expandIcon={<PlusIcon />} id='panel1a-header'>
                            <Typography>{`Log ${index + 1}`}</Typography>
                          </AccordionSummary>
                          <AccordionDetails className={classes.accordianDetails}>
                            {logs.map(log => {
                              return <Typography className={classes.log}>{log}</Typography>
                            })}
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )
                  })}
                </Box>
              </Tab>
            )}
          </Tabs>
        </Dialog>
      </>
    )
  }
)
